/**
 * Unified App Provider
 *
 * Single provider that handles:
 * - React Query (server state)
 * - Error boundaries
 * - Toast notifications (via event system)
 *
 * No longer needed:
 * - ThemeProvider (handled via CSS variables)
 * - AuthProvider (handled via Zustand store)
 * - TenantProvider (handled via Zustand store)
 * - PathnameProvider (use useLocation directly)
 * - ToastProvider (handled via event system)
 */

"use client";

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useState, useEffect } from "react";
// ✅ CRITICAL: Import ErrorBoundary directly, not from @/ui/shared barrel
// The barrel re-exports many shared components; prefer direct imports to keep
// Vite's dependency graph lean on startup.
import { ErrorBoundary } from "@/ui/shared/ErrorBoundary";
import { ToastContainer } from "@/ui/primitives/Toast";
import { subscribeToToast } from "@/services/shared/utils/toast";
import type { Toast } from "@/ui/primitives/Toast";
// ✅ CRITICAL: Import directly, not from @/services/shared/dependencies barrel
// The barrel exports registry, cache, hooks, etc. which might pull in domain code
import { DomainDependenciesProvider } from "@/services/shared/dependencies/domain-dependencies.context";
// ❌ REMOVED: initializeDomainDependencies import - causes all domain code to load on startup
// Domain dependencies are lazy-loaded when actually needed (after authentication)

/**
 * Toast Container Component
 * Subscribes to toast events and displays them
 */
function ToastContainerWrapper() {
  const [toasts, setToasts] = useState<Toast[]>([]);

  useEffect(() => {
    const unsubscribe = subscribeToToast((message, type, duration) => {
      const id = Math.random().toString(36).substring(7);
      const newToast: Toast = { id, message, type, duration };
      setToasts((prev) => [...prev, newToast]);
    });

    return unsubscribe;
  }, []);

  const removeToast = (id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  };

  return <ToastContainer toasts={toasts} onClose={removeToast} />;
}

/**
 * Unified App Provider
 */
export function AppProvider({ children }: { children: React.ReactNode }) {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 60 * 1000, // 1 minute
            gcTime: 5 * 60 * 1000, // 5 minutes
            retry: (failureCount, error) => {
              const status = (error as { statusCode?: number })?.statusCode;
              if (status === 429) return false;
              return failureCount < 1;
            },
            refetchOnWindowFocus: false,
            refetchOnMount: false,
            refetchOnReconnect: true,
            // Suppress AbortErrors (React Query cancellation on unmount)
            retryOnMount: false,
          },
          mutations: {
            retry: (failureCount, error) => {
              const status = (error as { statusCode?: number })?.statusCode;
              if (status === 429) return false;
              return failureCount < 1;
            },
          },
        },
      }),
  );

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <DomainDependenciesProvider>
          {children}
          <ToastContainerWrapper />
        </DomainDependenciesProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
